import { DictType } from "@pma/types";
import { useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { cn } from "@pma/utils/helpers";
import { Button } from "@components/common/button";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useAuth } from "@stores/auth-store";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  EDIT_USER_ORG_PARAMETER_ENDPOINT,
  SERVER_URL,
  USER_STATUS_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes";
import { toast } from "@hooks/use-toast";
import useTranslator from "@hooks/use-translator";

interface Props {
  status?: string;
  userId?: string;
  refetch: CallableFunction;
}
const UserStatusEditor = ({ status, userId, refetch }: Props) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(status);

  const { authConfig } = useAuth();
  const { translate } = useTranslator();

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const orgId = authConfig?.last_organization_id;
  const uId = authConfig?.id;

  const getUserStatuses = async () => {
    const response = await axios.get(
      SERVER_URL +
      `${USER_STATUS_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${uId}&for_task=true`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data } = useQuery({
    queryKey: ["user-statuses", orgId],
    queryFn: getUserStatuses,
  });

  const updateUserDetails = async (stat: DictType) => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,

      {
        user_id: userId,
        status_id: stat.id,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const updateUserStatusMutation = useMutation({
    mutationKey: [`status`],
    mutationFn: updateUserDetails,
    onSuccess: (data) => {
      setOpen(false);

      refetch && refetch();
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data.message ||
          translate(`Error occurred while updating`) +
          " " +
          translate(`status`),
        variant: "error",
      });
    },
  });
  useEffect(() => {
    status && setValue(status);
  }, [status]);

  if (userId) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex-col items-center md:flex w-fit h-7 rounded-md mx-2"
          >
            {value ? translate(value) : translate("Select Status")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0">
          <Command>
            <CommandInput placeholder={translate("Search status...")} />
            <CommandList>
              <CommandEmpty>{translate("No status found")}</CommandEmpty>
              <CommandGroup>
                {data?.map((status: DictType) => (
                  <CommandItem
                    key={status.id}
                    value={status?.name}
                    onSelect={(currentValue: any) => {
                      setValue(status?.name);
                      setOpen(false);

                      updateUserStatusMutation.mutate(status);
                    }}
                    className={cn("my-1 text-black cursor-pointer")}
                  >
                    {translate(status.name)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 text-black"
          style={{
            background: data?.find((status: DictType) => status?.name === value)
              ?.color,
          }}
        >
          {value
            ? data?.find((status: DictType) => status?.name === value)?.name
            : translate("Select Status")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder={translate("Search status...")} />
          <CommandList>
            <CommandEmpty>{translate("No status found")}</CommandEmpty>
            <CommandGroup>
              {data?.map((status: DictType) => (
                <CommandItem
                  key={status.id}
                  value={status?.name}
                  onSelect={(currentValue: any) => {
                    setValue(status?.name);
                    setOpen(false);
                    refetch && refetch(status);
                  }}
                  className={cn(
                    "my-1 text-white",
                    status?.color === "#transparent" && "bg-primary",
                  )}
                  style={{
                    background: status?.color,
                  }}
                >
                  {status.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default UserStatusEditor;
